import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Boxes, CheckCircle2, AlertTriangle } from "lucide-react";

export default function AcceptInvite() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [state, setState] = useState("checking"); // checking | valid | invalid
  const [invite, setInvite] = useState(null);
  const [checkError, setCheckError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); setCheckError("No invite token found in the link."); return; }
    api.get(`/auth/invite/${token}`)
      .then((r) => { setInvite(r.data); setState("valid"); })
      .catch((e) => { setCheckError(e.response?.data?.detail || "This invite link is invalid."); setState("invalid"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password });
      setSession(data.token, data.user);
      nav("/");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not set your password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#0B2545" }}>
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-16 text-white">
        <div className="font-head text-3xl font-bold tracking-widest">NETSTA<span style={{ color: "#00857C" }}>R</span></div>
        <div>
          <div className="overline mb-3" style={{ color: "#00857C" }}>Consignment Stock Platform</div>
          <h1 className="font-head text-5xl font-bold leading-tight">Welcome aboard.<br />Set your password.</h1>
          <p className="text-slate-300 mt-4 max-w-md">You're a few seconds away from tracking every consignment unit across the main bin, regional warehouses and fitment centres.</p>
        </div>
        <div className="text-slate-400 text-xs">Confidential — Netstar Consignment Stock Pilot</div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-[#F4F5F7] p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <Boxes className="text-[#00857C]" /> <span className="font-head font-bold text-xl text-[#0B2545]">NETSTAR</span>
          </div>

          {state === "checking" && (
            <div data-testid="invite-checking" className="flex items-center gap-2 text-slate-500 text-sm py-10">
              <Loader2 className="h-5 w-5 animate-spin text-[#00857C]" /> Checking your invite…
            </div>
          )}

          {state === "invalid" && (
            <div data-testid="invite-invalid">
              <div className="flex items-center gap-2 text-[#b45309] mb-3"><AlertTriangle className="h-6 w-6" /><span className="font-head text-xl font-bold">Invite unavailable</span></div>
              <p className="text-sm text-slate-600 mb-6">{checkError}</p>
              <Button data-testid="invite-go-login" onClick={() => nav("/login")} className="w-full rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 font-head">Go to sign in</Button>
            </div>
          )}

          {state === "valid" && (
            <>
              <div className="flex items-center gap-2 text-[#00857C] mb-1"><CheckCircle2 className="h-5 w-5" /><span className="overline">Invite verified</span></div>
              <h2 className="font-head text-2xl font-bold text-[#0B2545] mb-1">Set your password</h2>
              <p className="text-sm text-slate-500 mb-6">
                {invite?.name ? `${invite.name}, ` : ""}you're joining as <span className="font-medium text-[#0B2545]">{invite?.role_label}</span>.<br />
                Signing in as <span className="font-medium text-[#0B2545]">{invite?.email}</span>.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="overline text-slate-500">New password</Label>
                  <Input data-testid="invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="rounded-sm focus-visible:ring-[#00857C]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline text-slate-500">Confirm password</Label>
                  <Input data-testid="invite-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-sm focus-visible:ring-[#00857C]" />
                </div>
                {err && <div data-testid="invite-error" className="text-sm text-red-600">{err}</div>}
                <Button data-testid="invite-submit" disabled={loading} className="w-full rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set password & sign in"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
